import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import { Branch, Contact, MarketdateStatusEnum, Marketplace } from '../../entities';
import { SmsStatus } from '../../entities/log.model';
import { ApiService } from '../services/api.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AccountedMarketdateBooth } from '../../entities/accounted-marketdate-booth.model';
import { Principal, User } from '..';
import { Router } from '@angular/router';
import {BackofficeMarketdateRangeView} from '../../entities/backoffice-marketdate-range-view.model';
import {MarketOverviewViewEntry} from '../../entities/market-overview-view-entry.model';
import {Filter} from '../../interfaces/filter.interface';
import * as moment from 'moment';

@Component({
  selector: 'app-mass-sms',
  templateUrl: './marketwebsite-preview-mass-sms.component.html'
})
export class MarketwebsitePreviewMassSmsComponent implements OnInit {

  loadingOverlay: boolean;
  @Input() contacts: Array<Contact> = [];
  @Input() marketmasters: {
    markets: MarketOverviewViewEntry[],
    contact: Contact
  }[];
  @Input() recipientType: 'employees' | 'marketmasters' | 'merchantContactPersons';
  @Input() showBranchFilter: boolean;
  @Input() backToPlannedMarkets = false;
  @Input() numberOfWeeks = 56;
  @Input() message = '';
  @Input() marketplace;
  @Output() modalCloses = new EventEmitter(true);

  backToStr = 'Empfängern';
  contactSelection: Array<Contact>;
  contactSelectionStatus: Array<SmsStatus>;
  filteredMM: {
    markets: MarketOverviewViewEntry[],
    contact: Contact
  }[];
  modalState: string;
  sending: boolean;
  sent: boolean;

  recipientGroups: Array<{value: string, label: string}>;
  selectedRecipientGroupValue: string;

  branches: Array<Branch>;
  selectedBranchId: string;

  marketplaces: Marketplace[] = [];
  federalStates: string[] = [];
  selectedFederalStates: string[] = [];

  branchesWithCommunes: Array<{branch: Branch, communes: Array<string>}>;
  selectedCommune: string;

  marketdates: Array<BackofficeMarketdateRangeView>;
  daysWithMarketdates: Array<{dayString: string, dateString: string, marketdates: Array<BackofficeMarketdateRangeView>}>;
  selectedMarketdate: BackofficeMarketdateRangeView;
  selectedMarketdateId: number;

  costPerSms = 0.085;

  accountedMarketdateBooths: Array<AccountedMarketdateBooth>;
  filteredMarketdateBooths: Array<AccountedMarketdateBooth>;

  // Filters
  branchFilter: Filter;

  account: User;
  constructor(public activeModal: NgbActiveModal,
              private apiService: ApiService,
              private principal: Principal,
              private router: Router) {
    this.recipientGroups = [];
    this.principal.identity().then((account: User) => {
      this.account = account;
      if (account.authorities.some(role => role === 'ROLE_ADMIN')) {
        this.recipientGroups.push(
          { value: 'all', label: 'Alle Beschicker (unternehmensweit)' },
          { value: 'branch', label: 'Alle Beschicker einer bestimmten Niederlassung' }
        );
      }
      this.recipientGroups.push(
        { value: 'federalstate', label: 'Alle Beschicker eines bestimmten Bundeslandes' },
        { value: 'commune', label: 'Alle Beschicker einer bestimmten Kommune' },
        { value: 'marketdate', label: 'Alle Beschicker eines bestimmten Markttages' }
      );
    });
  }

  ngOnInit(): void {
    this.sent = this.sending = false;

    if (this.recipientType.toString() === 'employee' || this.recipientType.toString() === 'marketmasters') {
      this.backToStr = 'Mitarbeitern';
    }

    if (this.marketmasters) {
      this.initFilters();
      this.marketmasters.sort((a, b) => Number.parseInt(a.contact.merchantNumber) > Number.parseInt(b.contact.merchantNumber) ? 1 : -1);
      this.marketmasters.forEach(mm => {
        this.contacts.push(mm.contact);
      });
      this.filteredMM = [...this.marketmasters];
    }

    if (this.contacts) {
      this.contactSelection = [...this.contacts]
        .filter(selectedEmployee => selectedEmployee.mobile && selectedEmployee.mobile !== '');
      this.contactSelectionStatus = this.contactSelection.map(() => SmsStatus.None);
      this.modalState = 'recipients-' + this.recipientType;
    } else {
      this.loadingOverlay = true;
      Promise.all([this.getAccountedMarketdateBooths(), this.getBranches(), this.getMarketdates()])
        .then(() => {
          this.selectedRecipientGroupValue = null;
          this.selectedBranchId = null;
          this.selectedCommune = null;
          this.selectedMarketdate = null;
          this.selectedMarketdateId = null;
          this.modalState = 'recipientgroup';
          if (this.recipientGroups.length === 1) {
            this.selectedRecipientGroupValue = this.recipientGroups[0].value;
            this.recipientGroupSelected();
          }
          this.loadingOverlay = false;
        })
        .catch(e => {
          console.log('e', e);
          this.loadingOverlay = false;
          this.activeModal.dismiss('error getting data');
        });
    }
  }

  async initFilters() {
    this.branchFilter = {
      label: 'Filiale',
      options: [
        {
          value: 'all',
          label: 'alle Filialen'
        }
      ],
      selected: {
        value: 'all',
        label: 'alle Filialen'
      }
    };
    const branches = await this.apiService.getBranches();
    branches.forEach(branch => {
      this.branchFilter.options.push({
        value: branch.name,
        label: branch.name
      });
    });
  }

  changeFilter(filter, filterOption) {
    filter.selected = filterOption;
    this.contactSelection = [];

    if (filterOption.value === 'all') {
      this.filteredMM = [...this. marketmasters];
    } else {
      this.filteredMM = this.marketmasters.filter(mm => mm.contact.branchString === filterOption.value);
    }

    this.filteredMM.forEach(mm => {
      if (mm.contact.branchString === filterOption.value) {
        this.contactSelection.push(mm.contact);
      }
    });
  }

  getBranches(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.apiService.find<Branch>(Branch, 'branches/')
        .subscribe((branches: Array<Branch>) => {
          this.branches = branches
            .filter((branch: Branch) => branch.branchNumber !== 10 && branch.branchNumber !== 11)
            .sort((a: Branch, b: Branch) => a.branchNumber - b.branchNumber);
          this.branchesWithCommunes = this.branches.map((branch: Branch) => ({branch: branch, communes: []}))
            .sort((a, b) =>
              a.branch.branchNumber - b.branch.branchNumber);
          if (!this.account.authorities.some((role: string) => role === 'ROLE_ADMIN')) {
            this.branches = this.branches.filter((branch: Branch) => branch.name === this.account.contact.branchString
              || this.account.contact.branches.split('|').some(accountBranch => accountBranch === branch.name));
            this.branchesWithCommunes = this.branchesWithCommunes
              .filter((branchWithCommune: {branch: Branch, communes: Array<string>}) =>
                branchWithCommune.branch.name === this.account.contact.branchString
                || this.account.contact.branches.split('|').some(accountBranch => accountBranch === branchWithCommune.branch.name));
          }
          this.getMarketplaces().then(() => resolve());
        }, error => reject(error));
    });
  }

  getMarketplaces(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.apiService.find<Marketplace>(Marketplace, 'marketplaces/', {params: {size: 500}})
        .subscribe((marketplaces: Array<Marketplace>) => {
          this.marketplaces = [...marketplaces];
          for (const marketplace of marketplaces) {
            if (marketplace.city && marketplace.city !== '' && marketplace.branch &&
              this.branches.some(branch => branch.id === marketplace.branch.id) &&
              this.branchesWithCommunes
                .some(branchesWithCommune => branchesWithCommune.branch.id === marketplace.branch.id) &&
              !this.branchesWithCommunes
                .find(branchesWithCommune => branchesWithCommune.branch.id === marketplace.branch.id).communes
                .some(commune => commune === marketplace.city)
            ) {
              this.branchesWithCommunes
                .find(branchesWithCommune => branchesWithCommune.branch.id === marketplace.branch.id)
                .communes.push(marketplace.city);
            }
            if (marketplace.federalState) {
              if (!this.federalStates.some(item => item === marketplace.federalState)) {
                if (!this.account.authorities.some((role: string) => role === 'ROLE_ADMIN')) {
                  if (this.account.contact.branches.split('|').some(b => b === marketplace.branchString)) {
                    this.federalStates.push(marketplace.federalState);
                  }
                } else {
                  this.federalStates.push(marketplace.federalState);
                }
              }
            }
          }
          for (const branchWithCommune of this.branchesWithCommunes) {
            branchWithCommune.communes.sort((a, b) =>
              (a.toLowerCase() < b.toLowerCase()) ? -1 : (a.toLowerCase() > b.toLowerCase()) ? 1 : 0);
          }
          resolve();
        }, error => reject(error));
    });
  }

  getMarketdates(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const fromDate = new Date().getFullYear() + '-'
        + ('0' + (new Date().getMonth() + 1)).slice(-2) + '-'
        + ('0' + new Date().getDate()).slice(-2);
      const toDate = new Date(new Date().setDate(new Date().getDate() + 6)).getFullYear() + '-'
        + ('0' + (new Date(new Date().setDate(new Date().getDate() + 6)).getMonth() + 1)).slice(-2) + '-'
        + ('0' + new Date(new Date().setDate(new Date().getDate() + 6)).getDate()).slice(-2);
      this.apiService.findRegular<BackofficeMarketdateRangeView>(BackofficeMarketdateRangeView,
        'backoffice/marketdate-range-view/by-date-range/' + fromDate + '/' + toDate)
        .subscribe((marketdates: Array<BackofficeMarketdateRangeView>) => {
          // filter only unopened marketdates
          marketdates = marketdates.filter(md => md.status.toString() === MarketdateStatusEnum.Unopened.toString());

          // fix: new api uri delivers data independent of given daterange - thats why we filter here
          marketdates = marketdates.filter(md => moment(md.evasiveDate).isAfter(moment().subtract(1, 'day'), 'days')
            && moment(md.evasiveDate).isBefore(moment().add(7, 'days'), 'days'));

          // sort marketdates
          marketdates = marketdates.sort((a: BackofficeMarketdateRangeView, b: BackofficeMarketdateRangeView) =>
            new Date(a.evasiveDate).getTime() - new Date(b.evasiveDate).getTime());
          this.marketdates = marketdates;
          // make daysWithMarketdates array
          this.daysWithMarketdates =
            [...Array.from(new Set(marketdates.map((marketdate: BackofficeMarketdateRangeView) => marketdate.dayString)))]
              .map(dayString => ({
                dayString: dayString,
                dateString: moment(marketdates.find(md => md.dayString === dayString).evasiveDate).format('DD.MM.YYYY'),
                marketdates: marketdates
                  .filter(md => md.dayString === dayString)
                  .sort((a, b) => a.keyword.localeCompare(b.keyword))
              }));
          resolve();
        }, error => {
          console.log('error getting marketdates:', error);
          reject(error);
        });
    });
  }

  getAccountedMarketdateBooths(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.apiService.find<AccountedMarketdateBooth>(AccountedMarketdateBooth, 'accounted-marketdate-booths')
        .subscribe((accountedMarketdateBooths: Array<AccountedMarketdateBooth>) => {
          this.accountedMarketdateBooths = [];
          accountedMarketdateBooths = accountedMarketdateBooths
            .filter(amb => amb.cpStatus !== 'Deleted' && amb.cpStatus !== 'Changed');
          for (const accountedMarketdateBooth of accountedMarketdateBooths) {
            if (!this.accountedMarketdateBooths.some((amb: AccountedMarketdateBooth) => amb.cpId === accountedMarketdateBooth.cpId)) {
              this.accountedMarketdateBooths.push(accountedMarketdateBooth);
            }
          }
          const dayInMs = 86400000; // Number of milliseconds per day
          const date = new Date().getTime() - (dayInMs * 7 * this.numberOfWeeks);
          this.accountedMarketdateBooths = this.accountedMarketdateBooths.filter((amb) => new Date(amb.accountedAt).getTime() >= date);
          this.filteredMarketdateBooths = [...this.accountedMarketdateBooths];
          resolve();
        }, () => {
          reject();
        });
    });
  }

  /**
   * Returns an array of branches filtered by federal states.
   * Marketplaces are used as a reference, because 'Branch' class has no federalState property.
   */
  getBranchesByState(federalStates: string[]) {
    const markets = this.marketplaces.filter(market => federalStates.some(state => state === market.federalState || state === 'all'));
    return this.branches.filter(branch => markets.some(market => market.branch.name === branch.name));
  }

  /**
   * Filter contacts by federalStates
   */
  federalStateSelected(setFilter?) {
    if (!this.selectedFederalStates || this.selectedFederalStates.length < 1) { return; }
    if (setFilter) {
      this.filteredMarketdateBooths = [...this.accountedMarketdateBooths];
      this.filteredMarketdateBooths = this.filteredMarketdateBooths
      .filter((amb: AccountedMarketdateBooth) => this.selectedFederalStates.some(state => state === amb.mplaceFederalState));
    }
    if (this.selectedFederalStates.length  === 1 && this.selectedFederalStates[0] === 'all') {
      this.filteredMarketdateBooths = [...this.accountedMarketdateBooths];
    }

    this.contacts = this.filteredMarketdateBooths
      .filter((amb: AccountedMarketdateBooth) => this.selectedFederalStates
        .some(state => state === amb.mplaceFederalState || state === 'all'))
      .map(accountedMarketdateBooth => this.contactFromAccountedMarketdateBooth(accountedMarketdateBooth));
    this.contactSelection = [...this.contacts]
      .filter(contact => contact.mobile && contact.mobile !== '');
    this.contactSelectionStatus = this.contactSelection.map(() => SmsStatus.None);
  }

  recipientGroupSelected(): void {
    this.selectedCommune = null;
    this.selectedBranchId = null;
    if (this.selectedRecipientGroupValue === 'all') {
      this.contacts = this.filteredMarketdateBooths
        .map(accountedMarketdateBooth => this.contactFromAccountedMarketdateBooth(accountedMarketdateBooth));
      this.contactSelection = [...this.contacts]
        .filter(selectedEmployee => selectedEmployee.mobile && selectedEmployee.mobile !== '');
      this.contactSelectionStatus = this.contactSelection.map(() => SmsStatus.None);
    }
  }

  getContactCount(): number {
    return this.accountedMarketdateBooths.length;
  }

  branchSelected(setFilter?): void {
    if (!this.selectedBranchId || this.selectedBranchId === 'all') { return; }
    if (setFilter) {
      this.filteredMarketdateBooths = [...this.accountedMarketdateBooths];
      this.filteredMarketdateBooths = this.filteredMarketdateBooths
        .filter((amb: AccountedMarketdateBooth) => amb.mbBranchid === this.selectedBranchId);
    }

    this.contacts = this.filteredMarketdateBooths
      .filter((amb: AccountedMarketdateBooth) => amb.mbBranchid === this.selectedBranchId || this.selectedBranchId === 'all')
      .map(accountedMarketdateBooth => this.contactFromAccountedMarketdateBooth(accountedMarketdateBooth));
    this.contactSelection = [...this.contacts]
      .filter(selectedEmployee => selectedEmployee.mobile && selectedEmployee.mobile !== '');
    this.contactSelectionStatus = this.contactSelection.map(() => SmsStatus.None);
  }

  getContactCountByBranch(branchId: string, useFiltered?): number {
    if (useFiltered) {
      return this.filteredMarketdateBooths.filter((amb: AccountedMarketdateBooth) => amb.mbBranchid === branchId).length;
    }
    return this.accountedMarketdateBooths.filter((amb: AccountedMarketdateBooth) => amb.mbBranchid === branchId).length;
  }

  getContactCountByState(federalState: string, useFiltered?) {
    if (useFiltered) {
      return this.filteredMarketdateBooths.filter((booth) => booth.mplaceFederalState === federalState).length;
    }
    return this.accountedMarketdateBooths.filter((booth) => booth.mplaceFederalState === federalState).length;
  }

  getContactByStates(federalStates: string[]) {
    return this.accountedMarketdateBooths.filter((booth) => federalStates.some(state => state === booth.mplaceFederalState));
  }

  communeSelected(): void {
    this.contacts = this.accountedMarketdateBooths
      .filter((amb: AccountedMarketdateBooth) => amb.mpCity === this.selectedCommune)
      .map(accountedMarketdateBooth => this.contactFromAccountedMarketdateBooth(accountedMarketdateBooth));
    this.contactSelection = [...this.contacts]
      .filter(selectedEmployee => selectedEmployee.mobile && selectedEmployee.mobile !== '');
    this.contactSelectionStatus = this.contactSelection.map(() => SmsStatus.None);
  }

  marketdateSelected(): void {
    this.selectedMarketdate = this.marketdates.find(md => md.id.toString() === this.selectedMarketdateId.toString());
    if (this.selectedMarketdate) {
      if (this.selectedMarketdate.status.toString() === 'Unopened'
        || this.selectedMarketdate.status.toString() === 'Opened'
        || this.selectedMarketdate.status.toString() === 'Closed') {
        const route = (this.selectedMarketdate.status.toString() === 'Unopened')
          ? 'planning' : 'open-accountings';
          this.router.navigate([route + '/' + this.selectedMarketdate.id])
          .then(() => {
            this.modalState = 'marketdate-view';
          })
          .catch();
      }
    }
  }

  getContactCountByCommune(commune: string): number {
    return this.accountedMarketdateBooths.filter((amb: AccountedMarketdateBooth) => amb.mpCity === commune).length;
  }

  disableNextStep(): boolean {
    switch (this.modalState) {
      case 'recipientgroup':
        if (this.selectedRecipientGroupValue) {
          switch (this.selectedRecipientGroupValue) {
            case 'branch':
              return !this.selectedBranchId;
            case 'commune':
              return !this.selectedCommune;
            case 'marketdate':
            case 'all':
            default:
              return false;
          }
        } else {
          return true;
        }
      case 'recipients-employees':
      case 'recipients-marketmasters':
      case 'recipients-merchantContactPersons':
        return this.contactSelection.length === 0;
      case 'message':
        return this.message.length === 0;
      default:
        return true;
    }
  }

  isCheckedForMassSms(employee: Contact): boolean {
    return this.contactSelection.some(massSmsContact => massSmsContact.id === employee.id);
  }

  changeSelectionForMassSms(employee: Contact): void {
    if (this.isCheckedForMassSms(employee)) {
      this.contactSelection.splice(this.contactSelection.findIndex(i => i.id === employee.id), 1);
    } else {
      this.contactSelection.push(employee);
    }
  }

  selectByFunction(casFunction: string): void {
    this.contactSelection = [...this.contacts]
      .filter(contact => contact.mobile && contact.mobile !== '' &&
        ((casFunction && casFunction !== '') ? contact.casFunction === casFunction : true));
  }

  deselectAll(): void {
    this.contactSelection = [];
  }

  sendMassSms(single: boolean = false): void {
    if (single) {
      this.sending = true;
      Promise.all(this.contactSelection.map((contact, index) =>
        this.sendMassSmsSingle(contact, index)))
        .then(() => {
          this.sending = false;
          this.sent = true;
        })
        .catch(error => {
          console.log('error sending mass sms in single mode:', error);
          this.sending = false;
          this.sent = true;
        });
    } else {
      this.sending = true;
      this.contactSelectionStatus = this.contactSelectionStatus.map(() => SmsStatus.Pending);
      const massSmsData = {
        contacts: this.contactSelection,
        message: this.message
      };
      this.apiService.postRegular(massSmsData, 'mass-sms').subscribe(result => {
        const massSmsResult = result.json();
        this.sending = false;
        this.sent = true;
        this.contactSelectionStatus = this.contactSelectionStatus.map((currentStatus) =>
          (massSmsResult.some(item => this.contactSelection
            .some((contact) => contact.merchantNumber === item.merchantNumber))
            ? SmsStatus.Success : SmsStatus.Error));
      }, error => {
        console.log('error mass sms:', error);
        this.sending = false;
      });
    }
  }

  sendMassSmsSingle(contact: Contact, index: number) {
    return new Promise((resolve, reject) => {
      this.contactSelectionStatus[index] = SmsStatus.Pending;
      const massSmsData = {
        contacts: [contact],
        message: this.message
      };
      this.apiService.postRegular(massSmsData, 'mass-sms').subscribe(result => {
        const singleResult = result.json();
        console.log('singleResult:', singleResult);
        this.contactSelectionStatus[index] = SmsStatus.Success;
        resolve();
      }, error => {
        console.log('error sendMassSmsSingle:', error);
        this.contactSelectionStatus[index] = SmsStatus.Error;
        reject();
      });
    });
  }

  contactFromAccountedMarketdateBooth(accountedMarketdateBooth: AccountedMarketdateBooth): Contact {
    const contact = new Contact();
    contact.id = accountedMarketdateBooth.cpId;
    contact.firstname = accountedMarketdateBooth.cpFirstname;
    contact.lastname = accountedMarketdateBooth.cpLastname;
    contact.mobile = accountedMarketdateBooth.cpMobile;
    contact.casFunction = accountedMarketdateBooth.cpCasFunction;
    contact.merchantNumber = accountedMarketdateBooth.meMerchantNumber;
    contact.superordinateContact = new Contact();
    contact.superordinateContact.company = accountedMarketdateBooth.meCompany;
    contact.superordinateContact.merchantNumber = accountedMarketdateBooth.meMerchantNumber;
    contact.superordinateContact.companyAdditional = accountedMarketdateBooth.meCompanyAdditional;
    return contact;
  }

  marketdateViewBack() {
    this.selectedMarketdate = null;
    this.selectedMarketdateId = null;
    this.router.navigate(['messages']).then().catch();
    this.modalState = 'recipientgroup';
  }

  getSmsCount(count: number) {
    let num = Math.ceil(count / 160);
    if (num < 1) { num ++; }
    return num;
  }

}



// WEBPACK FOOTER //
// ./src/app/shared/marketwebsite-preview-mass-sms/marketwebsite-preview-mass-sms.component.ts